export const FILE_PREVIEW_LOADING_TITLE = 'Loading file';
export const FILE_PREVIEW_LOADING_HTML = `
  <style>
    :root {
      color-scheme: light;
      font-family: Arial, sans-serif;
    }

    body {
      margin: 0;
      min-height: 100vh;
      display: grid;
      place-items: center;
      background:
        radial-gradient(circle at top, #ffffff 0%, #eef4ff 38%, #e5edf9 100%);
      color: #334155;
    }

    .loading-state {
      display: grid;
      justify-items: center;
      gap: clamp(16px, 2.5vw, 24px);
      padding: clamp(24px, 5vw, 48px);
      text-align: center;
    }

    .spinner {
      width: clamp(40px, 7vw, 68px);
      height: clamp(40px, 7vw, 68px);
      border-radius: 50%;
      border: clamp(4px, 0.6vw, 6px) solid rgba(59, 130, 246, 0.16);
      border-top-color: #2563eb;
      animation: spin 0.9s linear infinite;
    }

    .loading-title {
      font-size: clamp(22px, 2.6vw, 34px);
      font-weight: 700;
      letter-spacing: -0.02em;
    }

    .loading-subtitle {
      max-width: min(90vw, 560px);
      font-size: clamp(14px, 1.3vw, 18px);
      line-height: 1.5;
      color: #64748b;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }

    @media (max-width: 640px) {
      .loading-state {
        padding-inline: 20px;
      }
    }
  </style>
  <div class="loading-state">
    <div class="spinner" aria-hidden="true"></div>
    <div class="loading-title">Loading file...</div>
    <div class="loading-subtitle">The preview will open automatically when the file is ready.</div>
  </div>
`;

export type FilePreviewHtmlOptions = {
  objectUrl: string;
  fileName: string;
  fileMimeType?: string | null;
  useMobileImagePreview?: boolean;
  returnUrl?: string;
};

export function buildFilePreviewHtml(opts: FilePreviewHtmlOptions): string {
  const normalizedMimeType = (opts.fileMimeType || '').toLowerCase();
  const shouldRenderImage = shouldRenderImagePreview(opts, normalizedMimeType);
  const shouldUseMobileImagePreview = shouldRenderImage && opts.useMobileImagePreview === true;
  const contentClass = buildPreviewContentClass(shouldRenderImage, shouldUseMobileImagePreview);
  const previewContent = buildPreviewContent(opts, normalizedMimeType, shouldRenderImage, shouldUseMobileImagePreview);

  return `
    <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes" />
    <style>
      :root {
        color-scheme: light;
        font-family: Arial, sans-serif;
      }

      html {
        height: 100%;
        height: -webkit-fill-available;
      }

      body {
        margin: 0;
        min-height: 100%;
        background: #0f172a;
        color: #e2e8f0;
      }

      .preview-layout {
        min-height: 100vh;
        min-height: 100dvh;
        min-height: -webkit-fill-available;
        display: grid;
        grid-template-rows: minmax(0, 1fr) auto;
      }

      .preview-content {
        display: grid;
        place-items: center;
        min-height: 0;
        overflow: hidden;
        padding: 16px;
      }

      .preview-content--image {
        display: block;
        padding: 0;
      }

      .preview-image {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }

      .preview-content--mobile-image {
        overflow: auto;
        padding: 0;
      }

      .preview-image--mobile {
        display: block;
        width: 100%;
        max-width: 100%;
        max-height: none;
        height: auto;
        margin: 0 auto;
      }

      .preview-frame {
        border: 0;
        width: 100%;
        height: 100%;
        background: #ffffff;
      }

      .preview-footer {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
        padding: 12px 16px calc(18px + env(safe-area-inset-bottom, 0px));
        background: rgba(15, 23, 42, 0.92);
        position: sticky;
        bottom: 0;
      }

      .preview-action {
        appearance: none;
        border: 1px solid rgba(191, 219, 254, 0.5);
        border-radius: 6px;
        background: rgba(15, 23, 42, 0.72);
        color: #bfdbfe;
        cursor: pointer;
        font: inherit;
        font-size: 18px;
        line-height: 1.2;
        padding: 10px 14px;
        text-align: center;
        text-decoration: none;
      }

      .preview-action:hover,
      .preview-action:focus-visible {
        background: rgba(30, 41, 59, 0.95);
        border-color: rgba(191, 219, 254, 0.9);
        outline: none;
      }

      .preview-link {
        text-decoration: underline;
      }

      @media (max-width: 640px) {
        .preview-content--mobile-image {
          padding: 0;
        }

        .preview-action {
          flex: 1 1 140px;
          font-size: 16px;
        }
      }
    </style>
    <div class="preview-layout">
      <div class="${contentClass}">
        ${previewContent}
      </div>
      <div class="preview-footer">
        ${buildFooterActions(opts.objectUrl, opts.returnUrl || '')}
      </div>
    </div>
    <script>
      (() => {
        const returnUrl = ${serializeScriptString(opts.returnUrl || '')};
        const closeButton = document.getElementById('preview-close-button');

        if (!closeButton) {
          return;
        }

        const getReturnUrl = () => {
          if (returnUrl) {
            return returnUrl;
          }

          try {
            return window.opener && !window.opener.closed ? window.opener.location.href : '';
          } catch {
            return '';
          }
        };

        const returnToApp = () => {
          const targetUrl = getReturnUrl();

          if (targetUrl) {
            window.location.replace(targetUrl);
            return;
          }

          if (window.history.length > 1) {
            window.history.back();
          }
        };

        closeButton.addEventListener('click', event => {
          event.preventDefault();

          try {
            window.close();
          } catch {
            returnToApp();
            return;
          }

          setTimeout(() => {
            if (!window.closed) {
              returnToApp();
            }
          }, 100);
        });
      })();
    </script>
  `;
}

function shouldRenderImagePreview(opts: FilePreviewHtmlOptions, normalizedMimeType: string): boolean {
  if (!normalizedMimeType.startsWith('image/')) {
    return false;
  }

  return normalizedMimeType !== 'image/svg+xml';
}

function serializeScriptString(value: string): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

function buildPreviewContentClass(shouldRenderImage: boolean, shouldUseMobileImagePreview: boolean): string {
  const classes = ['preview-content'];

  if (shouldRenderImage) {
    classes.push('preview-content--image');
  }

  if (shouldUseMobileImagePreview) {
    classes.push('preview-content--mobile-image');
  }

  return classes.join(' ');
}

function buildFooterActions(objectUrl: string, returnUrl: string): string {
  const goBackButton = `<a id="preview-close-button" class="preview-action" href="${escapeHtmlAttribute(
    returnUrl || '#'
  )}">Go back</a>`;

  return `${goBackButton}<a id="preview-download-link" class="preview-action preview-link" href="${escapeHtmlAttribute(
    objectUrl
  )}">Download file</a>`;
}

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function buildPreviewContent(
  opts: FilePreviewHtmlOptions,
  normalizedMimeType: string,
  shouldRenderImage: boolean,
  shouldUseMobileImagePreview: boolean
): string {
  if (shouldRenderImage) {
    return `<img class="preview-image${
      shouldUseMobileImagePreview ? ' preview-image--mobile' : ''
    }" src="${opts.objectUrl}" alt="Preview" />`;
  }

  return `
    <object class="preview-frame" data="${opts.objectUrl}" type="${normalizedMimeType || 'application/octet-stream'}">
      <iframe class="preview-frame" src="${opts.objectUrl}" title="${opts.fileName}"></iframe>
    </object>
  `;
}
